/**
 * AdminExercisesScreen — biblioteca completa de ejercicios para el admin.
 *
 * - Galería paginada con thumb GIF + nombre + músculo.
 * - Filtros: búsqueda (name/name_en), músculo, "solo sin GIF".
 * - Click en una card → modal de edición con TODOS los campos + preview del GIF.
 * - GIF: reemplazar subiendo archivo, descargando desde URL pública (Giphy/etc)
 *   o pegando una URL externa directa.
 * - Crear / eliminar ejercicio.
 *
 * Todos los cambios son globales (la tabla `exercise_library` se comparte con
 * AetherCoach). Al guardar/borrar se invalida el cache del CatalogContext para
 * que los entrenadores vean los cambios al instante.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  adminCreateExercise,
  adminDeleteExercise,
  adminExercisesMissingGifs,
  adminFetchExerciseGif,
  adminListExercises,
  adminListMuscles,
  adminUpdateExercise,
  adminUploadExerciseGif,
  type AdminExercise,
  type AdminExerciseInput,
  type MissingGifExercise,
} from "../../api/admin";
import { useCatalog } from "../../contexts/CatalogContext";
import { Icon } from "../../lib/Icon";
import { EmptyState } from "../../lib/components";
import { useToast } from "../../lib/toast";
import { AdminBadge, AdminGate } from "./_shared";

const PAGE_SIZE = 30;

export function AdminExercisesScreen() {
  return (
    <AdminGate>
      <AdminExercisesContent />
    </AdminGate>
  );
}

function AdminExercisesContent() {
  const toast = useToast();
  const { reload: reloadCatalog } = useCatalog();
  const [exercises, setExercises] = useState<AdminExercise[] | null>(null);
  const [muscles, setMuscles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [muscle, setMuscle] = useState<string>("");
  const [filterGif, setFilterGif] = useState<"all" | "with" | "without">("all");
  const [page, setPage] = useState(1);

  /** `null` = no hay modal. `"new"` = crear. Objeto = editar. */
  const [editing, setEditing] = useState<AdminExercise | "new" | null>(null);

  // Debounce de la búsqueda
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminListExercises({
        query: debouncedQuery || undefined,
        muscle: muscle || undefined,
        has_gif:
          filterGif === "with" ? true : filterGif === "without" ? false : undefined,
      });
      setExercises(list);
      setPage(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, muscle, filterGif]);

  useEffect(() => {
    load();
  }, [load]);

  // Cargar la lista de músculos UNA vez para los filtros + el dropdown del form.
  useEffect(() => {
    adminListMuscles().then(setMuscles).catch(() => setMuscles([]));
  }, []);

  const paged = useMemo(() => {
    if (!exercises) return [] as AdminExercise[];
    const start = (page - 1) * PAGE_SIZE;
    return exercises.slice(start, start + PAGE_SIZE);
  }, [exercises, page]);

  const totalPages = exercises ? Math.max(1, Math.ceil(exercises.length / PAGE_SIZE)) : 1;
  const countByGif = useMemo(() => {
    if (!exercises) return { withGif: 0, withoutGif: 0 };
    let w = 0;
    for (const e of exercises) if (e.video_url) w++;
    return { withGif: w, withoutGif: exercises.length - w };
  }, [exercises]);

  const onSave = async (payload: AdminExerciseInput, id?: number): Promise<AdminExercise | null> => {
    try {
      const saved = id == null
        ? await adminCreateExercise(payload)
        : await adminUpdateExercise(id, payload);
      toast(id == null ? `Ejercicio "${payload.name}" creado` : `"${payload.name}" actualizado`);
      reloadCatalog();
      await load();
      return saved;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast(`No pude guardar: ${msg}`);
      return null;
    }
  };

  const onDelete = async (ex: AdminExercise) => {
    if (!confirm(`¿Eliminar "${ex.name}" del catálogo global? Lo dejará de ver todo el equipo y los planes que lo referencian quedarán sin ese GIF.`)) {
      return;
    }
    try {
      await adminDeleteExercise(ex.id);
      toast(`"${ex.name}" eliminado`);
      reloadCatalog();
      await load();
    } catch (e) {
      toast("No pude eliminar: " + (e instanceof Error ? e.message : ""));
    }
  };

  return (
    <div className="page al-admin">
      <header className="al-admin-header">
        <div>
          <h1>
            Biblioteca de ejercicios <AdminBadge />
          </h1>
          <p className="muted">
            Catálogo completo (compartido con AetherCoach). Editá el nombre, el músculo,
            el GIF o creá ejercicios nuevos — los cambios se ven al instante para todos
            los entrenadores.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing("new")}>
          <Icon name="plus" size={14} /> Nuevo ejercicio
        </button>
      </header>

      <MissingGifsAlert
        onSearch={(name) => {
          setFilterGif("all");
          setMuscle("");
          setQuery(name);
        }}
      />

      <div className="al-admin-foods-toolbar">
        <div className="al-admin-foods-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Buscar por nombre (es/en) — fly, hack, press…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="link-btn" onClick={() => setQuery("")}>
              limpiar
            </button>
          )}
        </div>
        <select
          className="al-admin-foods-cat"
          value={muscle}
          onChange={(e) => setMuscle(e.target.value)}
        >
          <option value="">Todos los músculos</option>
          {muscles.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <div className="al-admin-ex-gif-filter">
          {(
            [
              ["all", "Todos"],
              ["with", "Con GIF"],
              ["without", "Sin GIF"],
            ] as Array<["all" | "with" | "without", string]>
          ).map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              className={filterGif === k ? "is-active" : ""}
              onClick={() => setFilterGif(k)}
            >
              {lbl}
            </button>
          ))}
        </div>
        <div className="tiny muted al-admin-foods-count">
          {exercises
            ? `${exercises.length} ejercicios · ${countByGif.withGif} con GIF · ${countByGif.withoutGif} sin GIF`
            : "—"}
        </div>
      </div>

      {error && (
        <EmptyState
          icon="x"
          title="Error al cargar el catálogo"
          body={error}
          action={
            <button className="btn btn-secondary" onClick={load}>
              Reintentar
            </button>
          }
        />
      )}

      {!error && loading && !exercises && (
        <div className="muted" style={{ padding: 24 }}>
          Cargando catálogo (1448 ejercicios)…
        </div>
      )}

      {!error && exercises && exercises.length === 0 && (
        <EmptyState
          icon="hammer"
          title="Sin resultados"
          body="Probá con otra búsqueda, otro músculo o limpiá los filtros."
          action={
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <Icon name="plus" size={14} /> Crear ejercicio
            </button>
          }
        />
      )}

      {!error && exercises && exercises.length > 0 && (
        <>
          <div className="al-admin-ex-grid">
            {paged.map((ex) => (
              <ExerciseCard
                key={ex.id}
                exercise={ex}
                onEdit={() => setEditing(ex)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          )}
        </>
      )}

      {editing && (
        <ExerciseForm
          exercise={editing === "new" ? null : editing}
          muscles={muscles}
          onClose={() => setEditing(null)}
          onSave={onSave}
          onDelete={editing !== "new" ? () => onDelete(editing as AdminExercise) : undefined}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// ExerciseCard — card del grid con GIF + nombre + músculo
// ----------------------------------------------------------------------

function ExerciseCard({ exercise, onEdit }: { exercise: AdminExercise; onEdit: () => void }) {
  // OJO con `loading="lazy"`: en grids paginadas con scroll de página (no del
  // contenedor) Chrome a veces falla en disparar el IntersectionObserver y la
  // imagen queda "pending" indefinidamente — el GIF está y funciona en el modal,
  // pero el thumb queda en blanco. Cargamos eager (la grilla es de máx ~30
  // tarjetas por página, los GIFs son ~200-400 KB cada uno → tolerable). El
  // onError cae al fallback si el archivo realmente no existe en disco.
  return (
    <button type="button" className="al-admin-ex-card" onClick={onEdit} title="Editar">
      <div className="al-admin-ex-card-thumb">
        {exercise.video_url ? (
          <img
            src={exercise.video_url}
            alt=""
            decoding="async"
            onError={(e) => {
              const el = e.currentTarget;
              el.style.display = "none";
              el.parentElement?.classList.add("is-broken");
            }}
          />
        ) : (
          <div className="al-admin-ex-card-fallback">
            <Icon name="hammer" size={24} />
            <span className="tiny">Sin GIF</span>
          </div>
        )}
      </div>
      <div className="al-admin-ex-card-body">
        <div className="al-admin-ex-card-name">{exercise.name}</div>
        <div className="al-admin-ex-card-meta">
          <span className="al-admin-ex-card-muscle">{exercise.muscle}</span>
          {exercise.equipment && (
            <span className="al-admin-ex-card-eq"> · {exercise.equipment}</span>
          )}
        </div>
        {exercise.name_en && (
          <div className="al-admin-ex-card-en">{exercise.name_en}</div>
        )}
      </div>
    </button>
  );
}

// ----------------------------------------------------------------------
// Pagination — controles de página
// ----------------------------------------------------------------------

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  // Mostrar páginas alrededor de la actual (max 7 items en la barra).
  const items: Array<number | "ellipsis"> = [];
  const push = (p: number | "ellipsis") => items.push(p);
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) push(i);
  } else {
    push(1);
    if (page > 3) push("ellipsis");
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    for (let i = start; i <= end; i++) push(i);
    if (page < totalPages - 2) push("ellipsis");
    push(totalPages);
  }
  return (
    <div className="al-admin-ex-pagination">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="al-admin-ex-page"
      >
        <Icon name="arrowL" size={12} /> Anterior
      </button>
      {items.map((it, i) =>
        it === "ellipsis" ? (
          <span key={`e${i}`} className="al-admin-ex-page-ellipsis">…</span>
        ) : (
          <button
            key={it}
            type="button"
            className={"al-admin-ex-page" + (it === page ? " is-active" : "")}
            onClick={() => onChange(it)}
          >
            {it}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="al-admin-ex-page"
      >
        Siguiente <Icon name="arrow" size={12} />
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// ExerciseForm — modal de alta/edición con preview de GIF + reemplazo
// ----------------------------------------------------------------------

interface ExerciseFormProps {
  exercise: AdminExercise | null; // null = crear
  muscles: string[];
  onClose: () => void;
  onSave: (payload: AdminExerciseInput, id?: number) => Promise<AdminExercise | null>;
  onDelete?: () => void | Promise<void>;
}

const COMMON_MUSCLES = [
  "Pecho",
  "Espalda alta",
  "Dorsal",
  "Lumbares",
  "Trapecio",
  "Hombro",
  "Bíceps",
  "Tríceps",
  "Antebrazo",
  "Cuádriceps",
  "Isquios",
  "Glúteo",
  "Aductores",
  "Abductores",
  "Pantorrilla",
  "Abdomen",
  "Core",
  "Cardio",
];

function ExerciseForm({ exercise, muscles, onClose, onSave, onDelete }: ExerciseFormProps) {
  const toast = useToast();
  const [name, setName] = useState(exercise?.name || "");
  const [nameEn, setNameEn] = useState(exercise?.name_en || "");
  const [muscle, setMuscle] = useState(exercise?.muscle || "");
  const [equipment, setEquipment] = useState(exercise?.equipment || "");
  const [videoUrl, setVideoUrl] = useState(exercise?.video_url || "");
  const [instructions, setInstructions] = useState(exercise?.instructions || "");
  const [secondary, setSecondary] = useState(exercise?.secondary_muscles || "");
  const [difficulty, setDifficulty] = useState(exercise?.difficulty || "");
  const [category, setCategory] = useState(exercise?.category || "");

  const [saving, setSaving] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Sugerencias de músculos combinando los del catálogo + estándar (para nuevos).
  const muscleOptions = useMemo(() => {
    const all = new Set([...muscles, ...COMMON_MUSCLES]);
    return Array.from(all).sort();
  }, [muscles]);

  const canSave = name.trim().length > 0 && muscle.trim().length > 0;

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const saved = await onSave(
        {
          name: name.trim(),
          name_en: nameEn.trim() || null,
          muscle: muscle.trim(),
          equipment: equipment.trim() || null,
          video_url: videoUrl.trim() || null,
          instructions: instructions.trim() || null,
          secondary_muscles: secondary.trim() || null,
          difficulty: difficulty.trim() || null,
          category: category.trim() || null,
        },
        exercise?.id,
      );
      if (saved) onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (uploading) return;
    setUploading(true);
    try {
      const res = await adminUploadExerciseGif(file);
      setVideoUrl(res.url);
      toast(`GIF subido (${res.file_size_kb} kB)`);
    } catch (e) {
      toast("Error al subir: " + (e instanceof Error ? e.message : ""));
    } finally {
      setUploading(false);
    }
  };

  const handleFetchFromUrl = async () => {
    const url = fetchingUrl.trim();
    if (!url || fetching) return;
    setFetching(true);
    try {
      const res = await adminFetchExerciseGif(url);
      setVideoUrl(res.url);
      setFetchingUrl("");
      toast(`Descargado desde internet (${res.file_size_kb} kB)`);
    } catch (e) {
      toast("No pude descargar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // BUG-FIX: el backdrop tiene que ENVOLVER al modal, no ser hermano.
    // Cuando eran hermanos: (a) el modal no se centraba (el `place-items:center`
    // del backdrop solo aplica a SUS hijos), por eso aparecía minúsculo arriba-
    // izquierda; (b) cualquier click sobre el modal subía al backdrop y lo
    // cerraba (no había nada que detuviera la propagación). Ahora el backdrop
    // hace `place-items: center`, el modal queda centrado a su max-width/height
    // y `stopPropagation` evita el cierre accidental al clickear adentro.
    <div className="al-modal-backdrop" onClick={onClose}>
      <div
        className="al-modal al-admin-ex-form"
        role="dialog"
        aria-label="Ejercicio"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
            {exercise ? `Editar “${exercise.name}”` : "Nuevo ejercicio"}
          </h3>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-admin-ex-form-body">
          {/* Columna izquierda: GIF preview + acciones */}
          <div className="al-admin-ex-form-gif">
            <div className="al-admin-ex-form-gif-preview">
              {videoUrl ? (
                <img src={videoUrl} alt="" />
              ) : (
                <div className="al-admin-ex-form-gif-empty">
                  <Icon name="hammer" size={32} />
                  <span className="tiny" style={{ marginTop: 6 }}>Sin GIF asignado</span>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/gif,image/webp,image/jpeg,image/png,video/mp4"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileUpload(f);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Icon name="upload" size={12} />{" "}
              {uploading ? "Subiendo…" : "Subir GIF desde tu equipo"}
            </button>

            <div className="al-admin-ex-form-fetch">
              <input
                type="text"
                className="al-input"
                placeholder="Pegá una URL de internet (Giphy, etc.)"
                value={fetchingUrl}
                onChange={(e) => setFetchingUrl(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={handleFetchFromUrl}
                disabled={fetching || !fetchingUrl.trim()}
                title="Descarga el GIF y lo guarda en nuestro servidor"
              >
                <Icon name="check" size={12} />{" "}
                {fetching ? "Descargando…" : "Bajar y guardar"}
              </button>
            </div>

            <div className="al-admin-ex-form-direct">
              <label className="tiny muted">URL del GIF (avanzado)</label>
              <input
                type="text"
                className="al-input"
                placeholder="https://… o /uploads/exercises/0015.gif"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
              {videoUrl && (
                <button
                  type="button"
                  className="link-btn tiny"
                  onClick={() => setVideoUrl("")}
                  style={{ marginTop: 4 }}
                >
                  Quitar GIF
                </button>
              )}
            </div>
          </div>

          {/* Columna derecha: campos */}
          <div className="al-admin-ex-form-fields">
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Nombre (español) *</span>
              <input
                type="text"
                className="al-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Sentadilla frontal con barra"
                autoFocus
              />
            </label>
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Nombre (inglés)</span>
              <input
                type="text"
                className="al-input"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder="Ej. Barbell Front Squat"
              />
            </label>

            <div className="al-admin-ex-form-grid2">
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Músculo principal *</span>
                <input
                  type="text"
                  className="al-input"
                  list="al-admin-ex-muscle-list"
                  value={muscle}
                  onChange={(e) => setMuscle(e.target.value)}
                  placeholder="Pecho, Espalda…"
                />
                <datalist id="al-admin-ex-muscle-list">
                  {muscleOptions.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </label>
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Equipamiento</span>
                <input
                  type="text"
                  className="al-input"
                  value={equipment}
                  onChange={(e) => setEquipment(e.target.value)}
                  placeholder="Barra, mancuerna, polea…"
                />
              </label>
            </div>

            <div className="al-admin-ex-form-grid2">
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Músculos secundarios</span>
                <input
                  type="text"
                  className="al-input"
                  value={secondary}
                  onChange={(e) => setSecondary(e.target.value)}
                  placeholder="Glúteo, core…"
                />
              </label>
              <label className="al-food-custom-field">
                <span className="tiny" style={{ fontWeight: 600 }}>Dificultad</span>
                <input
                  type="text"
                  className="al-input"
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  placeholder="Principiante / Intermedio / Avanzado"
                />
              </label>
            </div>

            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Categoría</span>
              <input
                type="text"
                className="al-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Compuesto, aislado, calistenia…"
              />
            </label>

            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>
                Cómo hacerlo (instrucciones)
              </span>
              <textarea
                className="al-input"
                rows={5}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Postura inicial, ejecución, errores comunes…"
              />
            </label>

            <div className="al-admin-ex-form-actions">
              {onDelete && (
                <button
                  type="button"
                  className="btn btn-ghost al-admin-ex-form-del"
                  onClick={onDelete}
                  disabled={saving}
                >
                  <Icon name="x" size={12} /> Eliminar
                </button>
              )}
              <div style={{ flex: 1 }} />
              <button className="btn btn-ghost" onClick={onClose} type="button" disabled={saving}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={submit}
                type="button"
                disabled={!canSave || saving}
              >
                {saving ? "Guardando…" : exercise ? "Guardar cambios" : "Crear ejercicio"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * MissingGifsAlert — alerta plegable que lista los ejercicios que los coaches
 * usaron en planes pero que NO resolvieron contra el catálogo (sin libId ni
 * gifUrl). Son candidatos a sumar al catálogo con su GIF (o a renombrar para
 * que matcheen). Click en un nombre → lo busca en la lista de abajo.
 */
function MissingGifsAlert({ onSearch }: { onSearch: (name: string) => void }) {
  const [items, setItems] = useState<MissingGifExercise[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    adminExercisesMissingGifs()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <div className="al-admin-missing-gifs">
      <button
        type="button"
        className="al-admin-missing-gifs-hd"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bell" size={15} />
        <span>
          <b>{items.length}</b> ejercicio{items.length === 1 ? "" : "s"} usado
          {items.length === 1 ? "" : "s"} en planes sin GIF en el catálogo
        </span>
        <span className="tiny muted" style={{ marginLeft: "auto" }}>
          {open ? "▾ ocultar" : "▸ ver lista"}
        </span>
      </button>
      {open && (
        <div className="al-admin-missing-gifs-body">
          <p className="tiny muted" style={{ margin: "0 0 8px" }}>
            Estos nombres los escribió un entrenador y no matchean ningún ejercicio del
            catálogo, así que en sus planes salen sin GIF ni link. Sumá el ejercicio (o
            renombrá el del catálogo para que coincida) y conseguí su GIF.
          </p>
          <div className="al-admin-missing-gifs-list">
            {items.map((it) => (
              <div key={it.name} className="al-admin-missing-gifs-row">
                <button
                  type="button"
                  className="link-btn"
                  title="Buscarlo en el catálogo"
                  onClick={() => onSearch(it.name)}
                >
                  {it.name}
                </button>
                <span className="tiny muted">
                  {it.plan_count} plan{it.plan_count === 1 ? "" : "es"} ·{" "}
                  {it.coach_count} coach{it.coach_count === 1 ? "" : "es"}
                  {it.coaches.length > 0 && ` (${it.coaches.join(", ")})`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
